import React, { useCallback, useEffect, useState } from 'react';

import { isEatsAppManager } from '../utils/eatsAppAuth';
import { canEditRestaurantStoreProfile } from './deliveryAgentSession';
import {
  PLATFORM_FEE_MODE_PER_ORDER_PERCENT,
  PLATFORM_FEE_MODE_SUBSCRIPTION,
  PLATFORM_SUBSCRIPTION_CADENCES,
  buildTaxPayload,
  createCompanyTax,
  emptyTaxForm,
  fetchCompanyTaxes,
  formatPlatformCommercialSummary,
  taxFormForEdit,
  taxRecordToForm,
  updateCompanyTax,
  validateTaxForm,
} from './companyTaxApi';
import './css/restaurantServices.css';

const TAX_FIELDS = [
  { key: 'gstPercent', label: 'GST %', step: '0.01', required: true },
  { key: 'cgstPercent', label: 'CGST %', step: '0.01', required: true },
  { key: 'sgstPercent', label: 'SGST %', step: '0.01', required: true },
  { key: 'deliveryFee', label: 'Delivery fee', step: '0.01', optional: true, isFee: true },
  { key: 'convienceFee', label: 'Convenience fee', step: '0.01', optional: true, isFee: true },
];

const formatDisplayValue = (value) => {
  if (value === '' || value == null) return '—';
  return String(value);
};

const CompanyTaxesDisplay = ({ restaurantId, storeId, hideTitle = false }) => {
  const [taxRecord, setTaxRecord] = useState(null);
  const [form, setForm] = useState(emptyTaxForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const canEditOwnerFields = canEditRestaurantStoreProfile();
  const canEditPlatformCommercial = isEatsAppManager();
  const canEdit = canEditOwnerFields || canEditPlatformCommercial;

  const entityId = restaurantId ?? storeId;
  const isStore = storeId != null && restaurantId == null;

  const loadTaxes = useCallback(async () => {
    const id = entityId != null ? String(entityId).trim() : '';
    if (!id || id === 'null' || id === 'undefined') {
      setTaxRecord(null);
      setForm(emptyTaxForm());
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const rows = await fetchCompanyTaxes(
        isStore ? { storeId: id } : { restaurantId: id },
      );
      const primary = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
      setTaxRecord(primary);
      setForm(taxRecordToForm(primary));
    } catch (e) {
      console.error('Failed to load tax configuration:', e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to load tax configuration.',
      );
      setTaxRecord(null);
      setForm(emptyTaxForm());
    } finally {
      setLoading(false);
    }
  }, [entityId, isStore]);

  useEffect(() => {
    loadTaxes();
  }, [loadTaxes]);

  const startEdit = () => {
    setForm(taxFormForEdit(taxRecord));
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setForm(taxRecordToForm(taxRecord));
    setIsEditing(false);
  };

  const handleFieldChange = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    const validationError = validateTaxForm(form, {
      includePlatformCommercial: canEditPlatformCommercial,
    });
    if (validationError) {
      window.alert(validationError);
      return;
    }

    const id = entityId != null ? String(entityId).trim() : '';
    if (!id) return;

    setSaving(true);
    try {
      const entityPayload = isStore ? { storeId: id } : { restaurantId: id };
      const payload = form.taxId
        ? buildTaxPayload(form, {
            includePlatformCommercial: canEditPlatformCommercial,
          })
        : buildTaxPayload(form, {
            ...entityPayload,
            includePlatformCommercial: canEditPlatformCommercial,
          });
      let saved;
      if (form.taxId) {
        saved = await updateCompanyTax(form.taxId, payload);
      } else {
        saved = await createCompanyTax(payload);
      }
      setTaxRecord(saved);
      setForm(taxRecordToForm(saved));
      setIsEditing(false);
      window.alert('Tax configuration saved successfully.');
    } catch (e) {
      console.error('Failed to save tax configuration:', e);
      window.alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          'Failed to save tax configuration.',
      );
    } finally {
      setSaving(false);
    }
  };

  const viewForm = taxRecord ? taxRecordToForm(taxRecord) : form;
  const platformSummary = formatPlatformCommercialSummary(viewForm);
  const editingMode = String(form.platformFeeMode || PLATFORM_FEE_MODE_PER_ORDER_PERCENT).toUpperCase();

  return (
    <div className="restaurant-detail-card restaurant-detail-taxes-card">
      <div
        className={`restaurant-detail-card-header restaurant-services-card-header${
          hideTitle ? ' restaurant-detail-card-header--badge-only' : ''
        }`}
      >
        {hideTitle ? null : <h3 className="restaurant-detail-section-title">Taxes</h3>}
        {canEdit && !isEditing ? (
          <button
            type="button"
            className="eats-admin-header-btn"
            onClick={startEdit}
            disabled={loading || saving}
          >
            {taxRecord ? 'Edit' : 'Configure'}
          </button>
        ) : null}
        {canEdit && isEditing ? (
          <div className="restaurant-detail-edit-actions restaurant-services-actions">
            <button
              type="button"
              className="eats-admin-header-btn"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="eats-admin-header-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </div>

      {loading ? <p className="restaurant-detail-message">Loading taxes…</p> : null}
      {!loading && error ? <p className="restaurant-detail-message error">{error}</p> : null}

      {!loading && !error ? (
        <>
          {isEditing ? (
            <div className="restaurant-detail-edit-form restaurant-taxes-form">
              {canEditOwnerFields ? (
                <div className="get-restaurant-form-row flex restaurant-taxes-form-grid">
                  {TAX_FIELDS.map((field) => (
                    <div key={field.key} className="get-restaurant-form-group">
                      <label>{field.label}</label>
                      <input
                        type="number"
                        min="0"
                        max={field.isFee ? undefined : '100'}
                        step={field.step}
                        value={form[field.key]}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        disabled={saving}
                        placeholder={field.optional ? 'Optional' : undefined}
                      />
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="restaurant-detail-platform-commercial">
                <h4 className="restaurant-detail-section-title">Platform commercial</h4>
                {canEditPlatformCommercial ? (
                  <div className="get-restaurant-form-row flex restaurant-taxes-form-grid">
                    <div className="get-restaurant-form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Mode</label>
                      <div className="restaurant-taxes-mode-options">
                        <label className="restaurant-taxes-mode-option">
                          <input
                            type="radio"
                            name="platformFeeMode"
                            value={PLATFORM_FEE_MODE_SUBSCRIPTION}
                            checked={editingMode === PLATFORM_FEE_MODE_SUBSCRIPTION}
                            onChange={(e) => handleFieldChange('platformFeeMode', e.target.value)}
                            disabled={saving}
                          />
                          Subscription (flat ₹)
                        </label>
                        <label className="restaurant-taxes-mode-option">
                          <input
                            type="radio"
                            name="platformFeeMode"
                            value={PLATFORM_FEE_MODE_PER_ORDER_PERCENT}
                            checked={editingMode === PLATFORM_FEE_MODE_PER_ORDER_PERCENT}
                            onChange={(e) => handleFieldChange('platformFeeMode', e.target.value)}
                            disabled={saving}
                          />
                          Per-order percent
                        </label>
                      </div>
                    </div>

                    {editingMode === PLATFORM_FEE_MODE_SUBSCRIPTION ? (
                      <>
                        <div className="get-restaurant-form-group">
                          <label>Cadence</label>
                          <select
                            value={form.platformSubscriptionCadence || ''}
                            onChange={(e) =>
                              handleFieldChange('platformSubscriptionCadence', e.target.value)
                            }
                            disabled={saving}
                          >
                            <option value="">Select…</option>
                            {PLATFORM_SUBSCRIPTION_CADENCES.map((cadence) => (
                              <option key={cadence} value={cadence}>
                                {cadence === 'ONE_TIME' ? 'One-time' : 'Monthly'}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="get-restaurant-form-group">
                          <label>Subscription amount (₹)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.platformSubscriptionAmount}
                            onChange={(e) =>
                              handleFieldChange('platformSubscriptionAmount', e.target.value)
                            }
                            disabled={saving}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="get-restaurant-form-group">
                        <label>Platform fee %</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={form.platformFeePercent}
                          onChange={(e) => handleFieldChange('platformFeePercent', e.target.value)}
                          disabled={saving}
                          placeholder="Optional"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="restaurant-detail-grid">
                    <div className="restaurant-detail-field">
                      <span className="restaurant-detail-label">Mode</span>
                      <span className="restaurant-detail-value">
                        {platformSummary?.modeLabel || '—'}
                      </span>
                    </div>
                    <div className="restaurant-detail-field">
                      <span className="restaurant-detail-label">Terms</span>
                      <span className="restaurant-detail-value">
                        {platformSummary?.detail || '—'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : taxRecord || platformSummary ? (
            <>
              <div className="restaurant-detail-grid">
                {TAX_FIELDS.map((field) => (
                  <div key={field.key} className="restaurant-detail-field">
                    <span className="restaurant-detail-label">{field.label}</span>
                    <span className="restaurant-detail-value">
                      {formatDisplayValue(viewForm[field.key])}
                    </span>
                  </div>
                ))}
              </div>
              <div className="restaurant-detail-platform-commercial">
                <h4 className="restaurant-detail-section-title">Platform commercial</h4>
                <div className="restaurant-detail-grid">
                  <div className="restaurant-detail-field">
                    <span className="restaurant-detail-label">Mode</span>
                    <span className="restaurant-detail-value">
                      {platformSummary?.modeLabel || '—'}
                    </span>
                  </div>
                  <div className="restaurant-detail-field">
                    <span className="restaurant-detail-label">Terms</span>
                    <span className="restaurant-detail-value">
                      {platformSummary?.detail || '—'}
                    </span>
                  </div>
                </div>
                {!canEditPlatformCommercial ? (
                  <p className="restaurant-detail-message">
                    Platform commercial terms are set by MyEats and shown here read-only.
                  </p>
                ) : null}
              </div>
            </>
          ) : (
            <p className="restaurant-detail-message">
              No tax configuration saved yet.
            </p>
          )}
        </>
      ) : null}
    </div>
  );
};

export default CompanyTaxesDisplay;
